import { useMemo } from 'react';

import { AgentProfileResponseDto } from '@shared/services/apiService';

const normalize = (value: string | undefined | null): string => (value ?? '').toLowerCase().trim();

// Lower score = better rank (exact > prefix > contains).
const rank = (haystack: string, needle: string): number => {
  if (!needle) return 0;
  const h = normalize(haystack);
  if (!h) return Number.POSITIVE_INFINITY;
  if (h === needle) return 0;
  if (h.startsWith(needle)) return 1;
  if (h.includes(needle)) return 2;
  return Number.POSITIVE_INFINITY;
};

const matches = (profile: AgentProfileResponseDto, query: string): number => {
  if (!query) return 0;
  const nameRank = rank(profile.name, query);
  if (nameRank !== Number.POSITIVE_INFINITY) return nameRank;
  const promptRank = rank(profile.prompt ?? '', query);
  if (promptRank !== Number.POSITIVE_INFINITY) return 10 + promptRank;
  return Number.POSITIVE_INFINITY;
};

export interface FilteredAgentProfilesResult {
  filteredProfiles: AgentProfileResponseDto[];
  totalCount: number;
  filteredCount: number;
  hasActiveSearch: boolean;
}

export const useFilteredAgentProfiles = (
  profiles: AgentProfileResponseDto[] | undefined,
  rawQuery: string,
): FilteredAgentProfilesResult => {
  return useMemo(() => {
    const safeList = profiles ?? [];
    const query = normalize(rawQuery);
    const hasActiveSearch = query.length > 0;
    const totalCount = safeList.length;

    if (!hasActiveSearch) {
      return {
        filteredProfiles: safeList,
        totalCount,
        filteredCount: totalCount,
        hasActiveSearch: false,
      };
    }

    const ranked = safeList
      .map((profile) => ({ profile, score: matches(profile, query) }))
      .filter((item) => item.score !== Number.POSITIVE_INFINITY)
      .sort((a, b) => {
        if (a.score !== b.score) return a.score - b.score;
        return normalize(a.profile.name).localeCompare(normalize(b.profile.name));
      });

    const filteredProfiles = ranked.map((item) => item.profile);

    return {
      filteredProfiles,
      totalCount,
      filteredCount: filteredProfiles.length,
      hasActiveSearch: true,
    };
  }, [profiles, rawQuery]);
};
